/**
 * Product Lot Entry
 * A single lot entry row. Each lot has its own unit configuration selector.
 * Contains: config selector, pallet/case/unit quantities, lot/batch numbers, mfg/expiry dates.
 */

import React from 'react';
import {
  Box, Typography, TextField, Grid, IconButton, Button, alpha, useTheme
} from '@mui/material';
import { Delete as DeleteIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

export default function ProductLotEntry({
  lot,
  lotIndex,
  totalLots,
  onOpenConfigDialog,
  onChange,
  onRemove,
  textFieldSx
}) {
  const theme = useTheme();

  const handleField = (field, value) => {
    onChange(lotIndex, { ...lot, [field]: value });
  };

  // Per-lot config
  const hasConfig = !!lot._configId;
  const enableCases = lot._enableCases ?? true;
  const enablePallets = lot._enablePallets ?? false;
  const unitsPerCase = parseInt(lot._unitsPerCase) || 1;
  const casesPerPallet = parseInt(lot._casesPerPallet) || 1;

  const pQty = parseInt(lot.palletQty) || 0;
  const cQty = parseInt(lot.caseQty) || 0;
  const uQty = parseInt(lot.unitQty) || 0;
  const totalBase = (pQty * casesPerPallet * unitsPerCase) + (cQty * unitsPerCase) + uQty;

  // Auto-calculate shelf life in months from mfg and expiry dates
  const shelfLifeMonths = (lot.mfgDate && lot.expiryDate)
    ? Math.round((new Date(lot.expiryDate) - new Date(lot.mfgDate)) / (1000 * 60 * 60 * 24 * 30.44))
    : null;

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
        bgcolor: alpha(theme.palette.grey[50], 0.4),
        position: 'relative'
      }}
    >
      {/* Header row with lot label and remove button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem' }}>
          Lot Entry {totalLots > 1 ? `#${lotIndex + 1}` : ''}
        </Typography>
        {totalLots > 1 && (
          <IconButton
            size="small"
            onClick={() => onRemove(lotIndex)}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) }
            }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Unit Configuration for this lot */}
      <Box sx={{ mb: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          onClick={() => onOpenConfigDialog(lotIndex)}
          startIcon={<SettingsIcon sx={{ fontSize: 15 }} />}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            height: 38,
            borderRadius: 2,
            backgroundColor: 'white',
            borderColor: hasConfig
              ? alpha(theme.palette.primary.main, 0.4)
              : alpha(theme.palette.grey[400], 0.8),
            color: hasConfig ? 'text.primary' : 'text.secondary',
            fontWeight: 500,
            px: 2,
            textTransform: 'none',
            fontSize: '0.82rem',
            '&:hover': { borderColor: theme.palette.primary.main }
          }}
        >
          {hasConfig
            ? `${lot._configName} — ${enableCases ? `${unitsPerCase} units/case` : 'Units only'}${enablePallets && enableCases ? ` · ${casesPerPallet} cases/pallet` : ''}`
            : 'Select Unit Configuration *'}
        </Button>
      </Box>

      {/* Quantities */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {hasConfig && enablePallets && (
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Pallets"
              type="number"
              value={lot.palletQty || ''}
              onChange={(e) => handleField('palletQty', e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              helperText={`= ${casesPerPallet} cases ea.`}
              sx={textFieldSx}
            />
          </Grid>
        )}
        {hasConfig && enableCases && (
          <Grid item xs={enablePallets ? 4 : 6}>
            <TextField
              fullWidth
              size="small"
              label="Cases"
              type="number"
              value={lot.caseQty || ''}
              onChange={(e) => handleField('caseQty', e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              helperText={`= ${unitsPerCase} units ea.`}
              sx={textFieldSx}
            />
          </Grid>
        )}
        <Grid item xs={hasConfig && enablePallets ? 4 : (hasConfig && enableCases ? 6 : 12)}>
          <TextField
            fullWidth
            size="small"
            label="Units"
            type="number"
            value={lot.unitQty || ''}
            onChange={(e) => handleField('unitQty', e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            sx={textFieldSx}
          />
        </Grid>
      </Grid>

      {/* Total indicator */}
      {totalBase > 0 && (
        <Box sx={{ mb: 2, py: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
            = {totalBase.toLocaleString()} total base units
          </Typography>
        </Box>
      )}

      {/* Lot & Batch */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            label="Lot Number"
            placeholder="Required"
            value={lot.lotNumber || ''}
            onChange={(e) => handleField('lotNumber', e.target.value)}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            label="Batch Number"
            placeholder="Required"
            value={lot.batchNumber || ''}
            onChange={(e) => handleField('batchNumber', e.target.value)}
            sx={textFieldSx}
          />
        </Grid>
      </Grid>

      {/* Dates */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Mfg Date"
              value={lot.mfgDate ? dayjs(lot.mfgDate) : null}
              onChange={(v) => handleField('mfgDate', v ? v.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', fullWidth: true, placeholder: 'Required', sx: textFieldSx } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expiry Date"
              value={lot.expiryDate ? dayjs(lot.expiryDate) : null}
              onChange={(v) => handleField('expiryDate', v ? v.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', fullWidth: true, placeholder: 'Required', sx: textFieldSx } }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      {/* Shelf Life (auto-calculated) */}
      {shelfLifeMonths !== null && shelfLifeMonths >= 0 && (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Shelf Life"
            value={`${shelfLifeMonths} month${shelfLifeMonths !== 1 ? 's' : ''}`}
            InputProps={{ readOnly: true }}
            helperText="Auto-calculated from Mfg & Expiry dates"
            sx={{
              ...textFieldSx,
              '& .MuiInputBase-input': {
                color: 'success.main',
                fontWeight: 500
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
}
